import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase BRANDING — TenantBrandProvider.
 *
 * Single source of truth for tenant logo / favicon / colors / display
 * name in the frontend. Loads from:
 *   • /api/tenant-branding          when authenticated
 *   • /api/public/tenant-branding   when anonymous (host-keyed)
 *
 * Never blocks page rendering. Any failure falls back to CertOpz
 * defaults so the app shell, login, register, and subscription pages
 * still render. Listens for `tenant-branding-updated` events so an
 * Admin Settings logo save propagates without logout.
 *
 * IMPORTANT: this provider is additive — it does NOT touch the payment
 * finalizer, webhook routing, checkout, invoice snapshots, or any
 * authorization path.
 */

// PRICING-MATRIX-ONLY follow-up (2026-02-27) — Default fallback logo now
// served from the local /public/logo.svg asset instead of a hardcoded
// external CDN URL. Tenant-uploaded and platform-uploaded logos
// continue to take precedence per the precedence rules below; this
// constant is ONLY used when neither is available.
const DEFAULT_LOGO_URL = "/logo.svg";

const DEFAULT_BRAND = {
  logoUrl: DEFAULT_LOGO_URL,
  // Phase BRANDING-V2 — symbol-only / icon variant for compact surfaces
  // (sidebar, topbar). Defaults to the same CertOpz logo so untouched
  // surfaces keep their existing look.
  symbolLogoUrl: DEFAULT_LOGO_URL,
  faviconUrl: null,
  appDisplayName: "CertOpz",
  organizationName: "",
  primaryColor: "#0B1F3A",
  secondaryColor: "#0891B2",
  accentColor: "#16A34A",
  poweredByCertOpz: true,
  workspaceUrl: null,
  entitlements: {
    custom_logo: false,
    login_logo: false,
    app_shell_logo: false,
    client_portal_logo: false,
    custom_colors: false,
    custom_favicon: false,
    white_label_domain: false,
  },
  loading: true,
  error: null,
};

const TenantBrandCtx = createContext({ ...DEFAULT_BRAND, refreshBranding: () => {} });

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function _absLogoUrl(url) {
  // Authenticated `/api/tenant-branding` returns `/api/files/{id}/download`.
  // Prefix with the backend host so the <img> request reaches the same
  // origin axios uses, otherwise CRA dev hosts will 404 on the relative URL.
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export function TenantBrandProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(DEFAULT_BRAND);
  // Tracks the auth mode currently reflected in `state` so we can drop a
  // stale authenticated payload after logout.
  const modeRef = useRef("public");

  const setSafe = useCallback((patch) => {
    setState((prev) => ({ ...prev, ...patch, loading: false }));
  }, []);

  const refreshBranding = useCallback(async () => {
    if (user) {
      modeRef.current = "auth";
      try {
        const { data } = await api.get("/tenant-branding");
        const d = data?.data || {};
        const fullLogo = _absLogoUrl(d.logo_url) || DEFAULT_LOGO_URL;
        // Phase BRANDING-V2 — symbol-only logo with full-logo fallback.
        // The backend already does the same fallback at the resolver
        // level; this duplicate is defensive against malformed payloads.
        const symbolLogo = _absLogoUrl(d.symbol_logo_url) || fullLogo;
        setSafe({
          logoUrl: fullLogo,
          symbolLogoUrl: symbolLogo,
          faviconUrl: _absLogoUrl(d.favicon_url),
          appDisplayName: d.app_display_name || "CertOpz",
          organizationName: d.organization_name || "",
          primaryColor: d.primary_color || "#0B1F3A",
          secondaryColor: d.secondary_color || "#0891B2",
          accentColor: d.accent_color || "#16A34A",
          poweredByCertOpz: d.powered_by_certopz !== false,
          workspaceUrl: d.workspace_url || null,
          entitlements: d.entitlements || DEFAULT_BRAND.entitlements,
          error: null,
        });
      } catch (e) {
        // Hard-fail safe: keep defaults so the app shell still renders.
        setSafe({ ...DEFAULT_BRAND, loading: false, error: "branding_unavailable" });
      }
      return;
    }
    modeRef.current = "public";
    // Try to fetch platform logo first (anonymous, no auth). Used as a
    // fallback ONLY when no tenant-specific logo applies for this host.
    // Authenticated tenant users do not reach this branch — their
    // tenant logo always wins (see the `if (user)` block above).
    let platformLogoUrl = null;
    let platformAppName = null;
    let platformLogoBgColor = null;
    let platformLogoBgKey = "default";
    try {
      const { data: pb } = await api.get(`/public/platform-branding`);
      const pd = pb?.data || {};
      platformLogoUrl = _absLogoUrl(pd.logo_url) || null;
      platformAppName = pd.app_display_name || null;
      platformLogoBgColor = pd.logo_background_color || null;
      platformLogoBgKey = pd.logo_background_key || "default";
    } catch (e) {
      /* platform branding is optional — silently skip */
    }
    try {
      const host = typeof window !== "undefined" ? window.location.host : "";
      const { data } = await api.get(`/public/tenant-branding`, { params: { host } });
      const d = data?.data || {};
      // Precedence: tenant logo (if set) > platform logo > built-in CertOpz default.
      const fullLogo = d.logo_data_uri || platformLogoUrl || DEFAULT_LOGO_URL;
      const symbolLogo = d.symbol_logo_data_uri || platformLogoUrl || fullLogo;
      // Only honor platform-logo background on anonymous surfaces when no
      // tenant logo applies (i.e., we are falling back to the platform logo).
      const usingPlatformLogo = !d.logo_data_uri && !!platformLogoUrl;
      setSafe({
        logoUrl: fullLogo,
        symbolLogoUrl: symbolLogo,
        faviconUrl: d.favicon_data_uri || null,
        appDisplayName: d.app_display_name || platformAppName || "CertOpz",
        organizationName: "",
        primaryColor: d.primary_color || "#0B1F3A",
        secondaryColor: "#0891B2",
        accentColor: d.accent_color || "#16A34A",
        poweredByCertOpz: d.powered_by_certopz !== false,
        workspaceUrl: null,
        entitlements: DEFAULT_BRAND.entitlements,
        platformLogoBgColor: usingPlatformLogo ? platformLogoBgColor : null,
        platformLogoBgKey:   usingPlatformLogo ? platformLogoBgKey   : "default",
        error: null,
      });
    } catch (e) {
      setSafe({ ...DEFAULT_BRAND, loading: false, error: "branding_unavailable" });
    }
  }, [user, setSafe]);

  // Re-fetch on auth changes.
  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  // Admin Settings logo upload dispatches this event; provider refreshes.
  useEffect(() => {
    const handler = () => { refreshBranding(); };
    window.addEventListener("tenant-branding-updated", handler);
    return () => window.removeEventListener("tenant-branding-updated", handler);
  }, [refreshBranding]);

  // Title + favicon side effects — only apply when the tenant is entitled
  // to white-label / favicon overrides. The base CertOpz title is kept
  // for everyone else so the marketing host always reads "CertOpz ERP".
  //
  // FAVICON OVERRIDE — INTENTIONALLY DISABLED (2026-08-06, Part E).
  // Tenant favicon storage, DB fields, and entitlement flags remain in
  // place for a future white-label / custom-domain phase, but the DOM
  // side-effect that swapped the browser tab icon is retired: the
  // platform favicon configured in Super Admin → Branding & Assets is
  // now the single, authoritative favicon on all current CertOpz
  // routes. This preserves the platform favicon after login, tenant
  // switch, HR/Payroll/Client Portal navigation, logout, and Super
  // Admin navigation.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (state.entitlements.white_label_domain && state.appDisplayName) {
      document.title = `${state.appDisplayName} | ERP`;
    } else {
      document.title = "CertOpz ERP";
    }
    // NOTE: no favicon mutation here. Runtime favicon installation is
    // owned by `installFaviconLinks()` in PlatformBrandingContext.
  }, [state.entitlements.white_label_domain, state.appDisplayName]);

  const value = useMemo(
    () => ({ ...state, refreshBranding }),
    [state, refreshBranding]
  );
  return <TenantBrandCtx.Provider value={value}>{children}</TenantBrandCtx.Provider>;
}

export const useTenantBrand = () => useContext(TenantBrandCtx);
